import type { ImageProps } from "@/app/directories/shop/[clientSlug]/types";
import { cn } from "@frontend/shared/lib/utils";

const truncateToSentences = (text: string | null, maxSentences = 2) => {
  if (!text) {
    return "";
  }
  // Split into sentences (handles multiple sentence-ending punctuation)
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [];

  if (sentences.length <= maxSentences) {
    return text;
  }

  // Join first maxSentences sentences and add ellipsis
  return `${sentences.slice(0, maxSentences).join("")}...`;
};

export const CustomCarouselSlide = ({
  item,
  isMinimal = false,
  imagePosition,
}: {
  item: {
    title: string;
    subtitle: string;
    description: string;
    image: ImageProps;
  };
  isMinimal?: boolean;
  imagePosition: "left" | "right";
}) => {
  return (
    <div
      className={cn(
        "keen-slider__slide flex items-start lg:!min-h-[560px] sm:!min-h-[560px] text-6xl font-medium text-black bg-transparent justify-center lg:gap-8 sm:gap-8 lg:px-8 sm:px-0 sm:py-0 relative",
        imagePosition === "right"
          ? "lg:flex-row-reverse sm:flex-col"
          : "lg:flex-row sm:flex-col",
      )}
    >
      <div className="rounded-[20px] h-full flex-1 flex flex-col justify-center flex-shrink-0 relative w-full">
        {/* Desktop Image */}
        <div className="lg:block sm:hidden w-full aspect-square rounded-[20px] relative overflow-hidden">
          <img
            src={item.image.url ?? ""}
            alt={item.image.alt ?? ""}
            className="w-full h-full object-cover rounded-[20px]"
          />
        </div>

        {/* Mobile Image */}
        <div className="lg:hidden sm:block w-full aspect-square rounded-[20px] overflow-hidden">
          <img
            src={item.image.url ?? ""}
            alt={item.image.alt ?? ""}
            className="w-full h-full object-cover rounded-[20px]"
          />
        </div>
        {/* Gradient overlay for text readability */}
        <div className="lg:hidden sm:block absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/20 rounded-[20px]" />

        <CustomCarouselSlideContentMobile item={item} isMinimal={isMinimal} />
      </div>
      <CustomCarouselSlideContent item={item} isMinimal={isMinimal} />
    </div>
  );
};

export const CustomCarouselSlideContentMobile = ({ item, isMinimal }) => {
  return (
    <div
      className={cn(
        "flex items-center lg:hidden sm:block absolute bottom-6 z-10",
      )}
    >
      <div className="flex flex-col items-start gap-0">
        <div className="p-3 flex flex-col gap-1 pl-[14px]">
          <p className="config-h4 lg:text-[24px] sm:text-[16px] font-bold text-white">
            {item.subtitle}
          </p>
          <h3 className="config-h3 lg:text-[40px] sm:text-[24px] text-white">
            {item.title.toUpperCase()}
          </h3>
          <p className="font-suisse font-light text-sm leading-[18px] w-full text-white">
            {truncateToSentences(item.description, 2)}
          </p>
        </div>
      </div>
    </div>
  );
};

export const CustomCarouselSlideContent = ({ item, isMinimal }) => {
  return (
    <div className={cn("flex-1 h-full w-full items-center lg:flex sm:hidden")}>
      <div className="flex-1 flex flex-col h-full items-start justify-center lg:gap-5 sm:gap-3 px-4 py-6 overflow-hidden">
        <h4 className="config-h4 lg:text-[24px] sm:text-[16px] text-black font-bold break-words">
          {item.subtitle}
        </h4>
        <h2 className="config-h2 lg:text-[40px] sm:text-[28px] text-black font-bold break-words leading-tight">
          {item.title}
        </h2>
        <p className="font-suisse font-light config-p3 lg:w-full sm:w-full break-words leading-relaxed">
          {item.description}
        </p>
      </div>
    </div>
  );
};
